/**
 * Armadillo's registry orders as an assignment bills them: the read-only
 * list of orders with what each adds to the fee, and, on a manual
 * assignment locked to Armadillo, how those orders set its fee (the Hub
 * writes it: their total plus any adjustment) and the warning that nothing
 * is billed until the assignment reaches Backoffice.
 */

import type { ReactNode } from 'react';

import { Alert, Group, Stack, Text } from '@mantine/core';

import { StatusBadge } from '@/components/StatusBadge';
import {
  describeRegistryFeeBreakdown,
  isLiveManualRegistryAssignment,
  isManagedAssignment,
  type BackofficeAssignment,
  type RegistryAssignmentOrder,
} from '@/types/np-assignments';
import {
  describeRegistryFee,
  effectiveRegistryOrderStatus,
  formatGbp,
  REGISTRY_ORDER_STATUS_META,
} from '@/types/registry-documents';

/** What the warning on an undispatched manual assignment says. */
export const NOT_IN_BACKOFFICE_YET =
  "Not in Backoffice yet: Armadillo's orders on this report are billed nowhere until this assignment is dispatched. Dispatch it, or unlock it to hand the orders back to the automatic assignment.";

/** The orders, one line each: reference, company, status, fee and source. */
export function RegistryOrderLines({ orders }: Readonly<{ orders: RegistryAssignmentOrder[] }>) {
  return (
    <Stack gap={2} role="list" aria-label="Registry orders">
      {orders.map((order) => {
        // The status the registry card shows: the worker's delivery
        // verdict on an order still awaiting delivery, else the stored one.
        const status = effectiveRegistryOrderStatus(order);
        const meta = REGISTRY_ORDER_STATUS_META[status] ?? {
          label: status,
          color: 'gray',
        };
        return (
          <Group
            key={order.id}
            gap={6}
            wrap="wrap"
            role="listitem"
            data-testid={`registry-assignment-order-${order.id}`}
          >
            <Text size="xs" ff="monospace">
              {order.reference}
            </Text>
            <Text size="xs" c="dimmed">
              {order.companyName}
            </Text>
            <StatusBadge label={meta.label} color={meta.color} size="xs" />
            <Text size="xs">
              {describeRegistryFee(order.fee, order.feeSource) ?? formatGbp(order.fee)}
            </Text>
          </Group>
        );
      })}
    </Stack>
  );
}

/** Whether this is a manual assignment holding Armadillo's orders. */
function holdsRegistryOrders(assignment: BackofficeAssignment): boolean {
  return !isManagedAssignment(assignment) && assignment.registryOrders.length > 0;
}

/**
 * The orders a manual Armadillo assignment bills, read-only: it took them
 * over from the automatic assignment, or was locked before the first
 * order. While the assignment is live the Hub writes its fee from them
 * (their total plus any adjustment); the fee field above shows that sum,
 * so the block does not repeat it. Nothing for any other assignment.
 */
export function ManualRegistryOrders({
  assignment,
}: Readonly<{ assignment: BackofficeAssignment }>) {
  if (!holdsRegistryOrders(assignment)) {
    return null;
  }
  return (
    <Stack gap={4} data-testid={`registry-orders-billed-${assignment.id}`}>
      <Text size="xs" c="dimmed" fw={500}>
        Registry orders billed here
      </Text>
      <RegistryOrderLines orders={assignment.registryOrders} />
      {/* Orders link only once they are billable, which is after Start, so
          a locked assignment holding them has simply not been sent yet. */}
      {assignment.status === 'np_locked' && (
        <Alert color="yellow" p="xs" data-testid={`registry-not-dispatched-${assignment.id}`}>
          <Text size="xs">{NOT_IN_BACKOFFICE_YET}</Text>
        </Alert>
      )}
    </Stack>
  );
}

/**
 * The description under a live manual Armadillo assignment's read-only
 * fee: how the registry orders set it (their total, any adjustment, the
 * fee). The fee itself, with a quote still pending called out, is the
 * field's value. Null for any other assignment: its fee is the analyst's
 * own.
 */
export function registryFeeHint(assignment: BackofficeAssignment): ReactNode {
  if (!isLiveManualRegistryAssignment(assignment)) {
    return null;
  }
  return (
    <span data-testid={`registry-fee-hint-${assignment.id}`}>
      {describeRegistryFeeBreakdown(assignment)}
    </span>
  );
}
